import FormWizard, {
  formVariantsEnum,
} from "../../../Forms/FormWizard/FormWizard";
import { useMemo, useCallback } from "react";
import DashboardStep from "./DashboardStep";
import NotificationSystemStep, {
  getCustomReportSubject,
} from "../NotificationSystemStep/NotificationSystemStep";
import AutomationSettingsStep from "../AutomationSettingsStep/AutomationSettingsStep";
import { useParams, useHistory } from "react-router-dom";
import { GET_AUTOMATION } from "../../AutomationsAPI";
import { useQuery } from "@apollo/client";
import useAutomationURLData, {
  AutomationURLDataReturn,
} from "../useAutomationURLData/useAutomationURLData";
import {
  validateAutomationData,
  AutomationTypeEnum,
  AutomationFormBaseFields,
} from "../../AutomationsUtils";
import { PageNotFound } from "@SeemplicityDev/shared";
import useAutomationSubmit from "../useAutomationSubmit/useAutomationSubmit";
import useAutomationWizardName from "../useAutomationWizardName/useAutomationWizardName";
import {
  integrationTypeToPageLinkMap,
  ticketEndpointTypeEnum,
} from "@SeemplicityDev/ticket-master";

export const DASHBOARD_REPORT_SUBJECT_DEFAULT =
  "Seemplicity Dashboard Report {{date}}";

interface DashboardReportFormFields extends AutomationFormBaseFields {
  dashboard_id: string;
  should_add_dashboard_link: boolean;
  ticketProvider: string;
  integrationType: string;
}

type DashboardReportOutputParams = {
  report_dashboard_id: string;
  should_add_dashboard_link: boolean;
  ticket_provider_id: string;
  keys: Record<string, any>;
  report_subject?: string;
};

type DashboardReportInputParams = {
  report_dashboard_id: string;
  ticket_provider_id: string;
  ticket_provider_type: string;
  field_mapping_id: string;
  should_add_dashboard_link: boolean;
  report_subject?: string;
};

const validateDashboardReportInputParams = (params: any): boolean => {
  if (!params) {
    return false;
  }

  try {
    const parsedParams = JSON.parse(params);

    return (
      parsedParams.report_dashboard_id &&
      parsedParams.ticket_provider_id &&
      parsedParams.ticket_provider_type &&
      parsedParams.field_mapping_id &&
      typeof parsedParams.should_add_dashboard_link !== "undefined"
    );
  } catch (e) {
    return false;
  }
};

const DashboardReport: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const automationURLData = useAutomationURLData();
  const { onSubmit, createAutomationLoading, editAutomationLoading } =
    useAutomationSubmit({
      automationType: AutomationTypeEnum.DASHBOARD_REPORT,
      automationId: id,
    });

  const { data, loading } = useQuery(GET_AUTOMATION, {
    variables: {
      id,
    },
    skip: !id,
  });

  const automationData = useMemo(() => {
    if (
      !!data?.get_automation &&
      validateAutomationData(
        data?.get_automation,
        validateDashboardReportInputParams
      )
    ) {
      const automation = data?.get_automation;
      const params: DashboardReportInputParams = JSON.parse(
        automation?.action?.params
      );

      return { automation, params };
    }
  }, [data]);

  const wizardData:
    | DashboardReportFormFields
    | AutomationURLDataReturn
    | undefined = useMemo(() => {
    if (!!automationData) {
      const { automation, params } = automationData;

      return {
        dashboard_id: params.report_dashboard_id,
        should_add_dashboard_link: params.should_add_dashboard_link,
        filter: {
          value: {
            id: automation.selection_rule?.filtersid,
          },
        },
        scope: {
          value: {
            id:
              automation.selection_rule?.scopesid ||
              automation.selection_rule?.scope_group,
          },
        },
        name: automation.name,
        schedule: automation.trigger.value,
        ticketProvider: params.ticket_provider_id,
        integrationType: params.ticket_provider_type,
        report_subject:
          params.report_subject ?? DASHBOARD_REPORT_SUBJECT_DEFAULT,
      };
    } else if (!!automationURLData) {
      return automationURLData;
    }
  }, [automationData, automationURLData]);

  const wizardName = useAutomationWizardName(
    "Dashboard Report",
    automationData?.automation?.name
  );

  const steps = useMemo(() => {
    return [
      {
        name: "Dashboard",
        id: "dashboard",
        form: <DashboardStep />,
      },
      {
        name: "Notification System",
        id: "notification_system",
        form: (
          <NotificationSystemStep
            fieldMappingId={automationData?.params?.field_mapping_id}
            reportSubjectDefault={DASHBOARD_REPORT_SUBJECT_DEFAULT}
          />
        ),
      },
      {
        name: "Scheduling",
        id: "scheduling",
        form: <AutomationSettingsStep />,
      },
    ];
  }, [automationData?.params]);

  const handleSubmit = useCallback(
    (data: Record<string, any>) => {
      const reportSubject = getCustomReportSubject(
        data.report_subject,
        DASHBOARD_REPORT_SUBJECT_DEFAULT
      );
      const params: DashboardReportOutputParams = {
        report_dashboard_id: data.dashboard_id,
        should_add_dashboard_link: data.should_add_dashboard_link,
        ticket_provider_id: data.ticketProvider,
        keys: data.keys,
        ...(reportSubject && { report_subject: reportSubject }),
      };

      onSubmit(
        {
          filter: data.filter,
          scope: data.scope,
          name: data.name,
          schedule: data.schedule,
          params,
        },
        () => {
          history.push(
            `/remediation/monitoring/${integrationTypeToPageLinkMap[ticketEndpointTypeEnum.REPORT]}`
          );
        }
      );
    },
    [id]
  );

  return id && !automationData && !loading ? (
    <PageNotFound
      subtitle={"Oops! Something went wrong..."}
      description={"For help, contact Customer Services"}
      showReachOutButton={false}
    />
  ) : (
    <FormWizard
      submitLabel={`${id ? "Save" : "Create"}`}
      originName={`${id ? "Edit" : "Add"} Automation`}
      wizardName={wizardName}
      steps={steps}
      onCancel={() => {
        history.goBack();
      }}
      wizardData={wizardData}
      onSubmit={handleSubmit}
      isSubmitting={id ? editAutomationLoading : createAutomationLoading}
      originLink={undefined}
      dangerSubmit={false}
      className={""}
      withSummary={true}
      type={formVariantsEnum.PAGE}
      isValidated={Boolean(id)}
    />
  );
};

export default DashboardReport;
